import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Information } from "src/components/login/Information";
import CmtImage from "src/@coremat/CmtImage";
import { useSelector } from "react-redux";
import { ROLES } from "src/helpers/constants";
import { validateToken } from "src/api/auth";
import Alert from "src/helpers/alert";

const AccountType = () => {
  const navigate = useNavigate();
  const { tenant } = useSelector(({ auth }) => auth);
  const [selectedRole, setSelectedRole] = useState("");
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [tokenValue, setTokenValue] = useState(null);

  useEffect(() => {
    const validateTokenFromUrl = async () => {
      const usp = new URLSearchParams(window.location.search);
      const token = usp.get("token");
      
      if (!token) return;

      try {
        setIsValidatingToken(true);
        const response = await validateToken(token);
        const role = response.role || (response.data && response.data.role);
        
        if (role) {
          setSelectedRole(role);
          setHasValidToken(true);
          setTokenValue(token);
        } else {
          console.error('No role found in response:', response);
          Alert.error('Invalid token response');
        }
      } catch (error) {
        console.error('Error validating token:', error);
        Alert.error('Invalid token');
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateTokenFromUrl();
  }, []);

  const handleRoleSelect = (event) => {
    if (!hasValidToken) {
      setSelectedRole(event.target.value);
    }
  };

  const handleContinue = () => {
    if (!selectedRole) {
      return;
    }

    if (hasValidToken) {
      // If we have a valid token, always go to signup with both token and role
      navigate("/signup", { 
        state: { 
          role: selectedRole,
          token: tokenValue
        }
      });
    } else {
      // No token flow - check role for navigation
      if (selectedRole === ROLES.FOUNDER) {
        navigate("/signup", { 
          state: { role: selectedRole }
        });
      } else {
        navigate("/schedule-call", { 
          state: { role: selectedRole }
        });
      }
    }
  };

  return (
    <Box display="flex" minHeight="100vh" width="100vw" className="bg-white">
      {/* Left column: Information */}
      <Information />
      {/* Right column: Account type selection */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flexGrow={1}
        width="100%"
        minHeight="100vh"
        className="bg-white"
      >
        <Box width="100%" maxWidth={400} mx="auto" display="flex" flexDirection="column" alignItems="center">
          <CmtImage
            alt="InvestorMatch Logo"
            height="100px"
            style={{ margin: "0 auto 32px auto", display: "block", width: "15vw", minWidth: 120, maxWidth: 200 }}
            src={(tenant?.logo || {})["2x"]?.path || "/images/logo-v1.png"}
          />
          <Typography
            variant="h6"
            align="left"
            style={{ fontWeight: 600, fontSize: 22, color: "#446475", marginBottom: 24, width: '100%' }}
          >
            {hasValidToken ? 'Your Account Type' : 'Select Account Type'}
          </Typography>
          <RadioGroup 
            value={selectedRole} 
            onChange={handleRoleSelect} 
            style={{ width: "100%", marginBottom: 32 }}
          >
            <FormControlLabel
              value={ROLES.FOUNDER}
              control={<Radio color="primary" disabled={hasValidToken} />}
              label="Founder/Entrepreneur"
              style={{ 
                width: "100%", 
                margin: "0 0 16px 0",
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: selectedRole === ROLES.FOUNDER ? "#f3f4f6" : "#f9fafb",
                color: "#446475",
                fontSize: 18,
                fontWeight: 500,
                minHeight: 56,
                cursor: hasValidToken ? "default" : "pointer",
                opacity: hasValidToken && selectedRole !== ROLES.FOUNDER ? 0.5 : 1
              }}
            />
            <FormControlLabel
              value={ROLES.FUNDER}
              control={<Radio color="primary" disabled={hasValidToken} />}
              label="Investor/Funder"
              style={{ 
                width: "100%", 
                margin: "0 0 16px 0",
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: selectedRole === ROLES.FUNDER ? "#f3f4f6" : "#f9fafb",
                color: "#446475",
                fontSize: 18,
                fontWeight: 500,
                minHeight: 56,
                cursor: hasValidToken ? "default" : "pointer",
                opacity: hasValidToken && selectedRole !== ROLES.FUNDER ? 0.5 : 1
              }}
            />
            <FormControlLabel
              value={ROLES.VENDOR}
              control={<Radio color="primary" disabled={hasValidToken} />}
              label="Vendor/Service Provider"
              style={{ 
                width: "100%", 
                margin: 0,
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: selectedRole === ROLES.VENDOR ? "#f3f4f6" : "#f9fafb",
                color: "#446475",
                fontSize: 18,
                fontWeight: 500,
                minHeight: 56,
                cursor: hasValidToken ? "default" : "pointer",
                opacity: hasValidToken && selectedRole !== ROLES.VENDOR ? 0.5 : 1
              }}
            />
          </RadioGroup>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleContinue}
            disabled={!selectedRole || isValidatingToken}
            style={{ height: 56, fontWeight: 600, fontSize: 18, width: "100%" }}
          >
            {isValidatingToken ? "Validating..." : "Continue"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountType; 